/**
 * Import a word list into the tenant-wide Allow List (sc-11468).
 *
 * 1. Pick a CSV / XLSX file — any layout. The server proposes how its columns
 *    map onto phrases, categories and match types (the internal LLM reads only
 *    the header and the first rows; heuristics when it is unavailable).
 * 2. Preview: the mapping is editable (each edit re-runs the preview), with
 *    totals per category, duplicates, conflicts and errors.
 * 3. Add to list, or Replace list (confirmed: entries not in the file are
 *    removed). Either way the server rescans open cases once.
 *
 * The file stays in the browser between steps and is sent again with the
 * mapping on every call; the server keeps nothing between preview and import.
 */
import { useId, useRef, useState } from 'react';
import { AlertTriangle, FileUp, Loader2, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/Button';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  apiHelpers,
  dictionaryApi,
  type DictionaryAction,
  type ImportColumnMapping,
  type ImportMatch,
  type ImportPreviewResponse,
  type ImportPreviewRow,
  type ImportResultResponse,
} from '../../lib/api';
import {
  STATUS_LABELS,
  withCategoryColumn,
  withCategoryMatch,
  withColumn,
  withLayout,
} from '../../lib/dictionaryImport';

const ACCEPT = '.csv,.tsv,.txt,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const NONE = 'none';
const ROWS_SHOWN = 100;

const SOURCE_TEXT: Record<ImportPreviewResponse['mapping_source'], string> = {
  llm: 'Columns mapped by the AI model from the header and first rows. Check them below.',
  heuristic: 'Columns mapped from the header names (the AI model was not available). Check them below.',
  user: 'Your column mapping.',
};

const plural = (n: number | undefined, word: string) => (n === 1 ? word : `${word}s`);

const STATUS_CLASS: Record<ImportPreviewRow['status'], string> = {
  new: 'bg-green-50 text-green-800 border-green-200',
  update: 'bg-blue-50 text-blue-800 border-blue-200',
  unchanged: 'bg-gray-50 text-gray-700 border-gray-200',
  duplicate: 'bg-amber-50 text-amber-800 border-amber-200',
  conflict: 'bg-orange-50 text-orange-800 border-orange-200',
  error: 'bg-red-50 text-red-700 border-red-200',
};

interface AllowListImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful import so the list can refresh. */
  onImported: (result: ImportResultResponse) => void;
  action?: DictionaryAction;
  listLabel?: string;
}

export function AllowListImportDialog({
  open,
  onOpenChange,
  onImported,
  action = 'always_allow',
  listLabel = 'Allow List',
}: AllowListImportDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResultResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const requestSeq = useRef(0);
  const fileFieldId = useId();

  const reset = () => {
    setFile(null);
    setPreview(null);
    setLoading(false);
    setError(null);
    setConfirmReplace(false);
    setImporting(false);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleOpenChange = (next: boolean) => {
    if (importing) return;
    if (!next) reset();
    onOpenChange(next);
  };

  const runPreview = async (target: File, mapping?: ImportColumnMapping) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    setConfirmReplace(false);
    try {
      const response = await dictionaryApi.previewImport(target, action, mapping);
      if (seq === requestSeq.current) setPreview(response);
    } catch (err) {
      if (seq === requestSeq.current) setError(apiHelpers.handleError(err));
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  };

  const handleFile = (picked: File | null) => {
    setFile(picked);
    setPreview(null);
    setResult(null);
    if (picked) runPreview(picked);
  };

  const updateMapping = (mapping: ImportColumnMapping) => {
    if (file) runPreview(file, mapping);
  };

  const handleImport = async (mode: 'add' | 'replace') => {
    if (!file || !preview) return;
    setImporting(true);
    setError(null);
    try {
      const response = await dictionaryApi.runImport(file, action, preview.mapping, mode);
      setResult(response);
      setConfirmReplace(false);
      onImported(response);
    } catch (err) {
      setError(apiHelpers.handleError(err));
    } finally {
      setImporting(false);
    }
  };

  const totals = preview?.totals ?? {};
  const importable = (totals.new ?? 0) + (totals.update ?? 0);
  const canImport = !!preview && !preview.mapping_problems.length && !loading && !importing;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="allow-list-import-dialog">
        <DialogHeader>
          <DialogTitle>Import {listLabel}</DialogTitle>
          <DialogDescription>
            Upload a CSV or Excel file in any layout: one phrase per row, or one column per category. Nothing is
            saved until you choose Add to list or Replace list.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <ImportResult result={result} listLabel={listLabel} />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <label htmlFor={fileFieldId} className="sr-only">
                File to import
              </label>
              <input
                id={fileFieldId}
                ref={fileInputRef}
                type="file"
                accept={ACCEPT}
                onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
                className="text-sm file:mr-3 file:rounded-md file:border file:border-border file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-muted/40"
              />
              {loading && (
                <span className="inline-flex items-center gap-2 text-sm text-muted-foreground" role="status">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  {preview ? 'Updating preview…' : 'Reading the file…'}
                </span>
              )}
            </div>

            {error && (
              <div role="alert" className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
                {error}
              </div>
            )}

            {!file && !error && (
              <div className="flex items-center gap-3 rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
                <FileUp className="h-5 w-5 shrink-0" aria-hidden="true" />
                Columns like phrase, match (exact or contains) and category are read directly; any other layout is
                mapped for you and shown here before anything is saved.
              </div>
            )}

            {preview && (
              <ImportPreview
                preview={preview}
                disabled={loading || importing}
                onMappingChange={updateMapping}
              />
            )}

            {confirmReplace && preview && (
              <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                <p className="font-medium">Replace the whole {listLabel}?</p>
                <p className="mt-1">
                  {(totals.remove ?? 0).toLocaleString()} current {totals.remove === 1 ? 'entry' : 'entries'} not in
                  this file will be removed, and the list will hold exactly the {(
                    (totals.new ?? 0) +
                    (totals.update ?? 0) +
                    (totals.unchanged ?? 0)
                  ).toLocaleString()}{' '}
                  importable phrases. Open cases are re-scanned once.
                </p>
              </div>
            )}
          </>
        )}

        <DialogFooter>
          {result ? (
            <Button onClick={() => handleOpenChange(false)}>Done</Button>
          ) : confirmReplace ? (
            <>
              <Button variant="outline" onClick={() => setConfirmReplace(false)} disabled={importing}>
                Back
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => handleImport('replace')}
                disabled={!canImport}
                aria-busy={importing}
              >
                {importing && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                Replace list
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={importing}>
                Cancel
              </Button>
              <Button
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
                onClick={() => setConfirmReplace(true)}
                disabled={!canImport}
              >
                Replace list…
              </Button>
              <Button onClick={() => handleImport('add')} disabled={!canImport || importable === 0} aria-busy={importing}>
                {importing && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                Add to list{preview && importable ? ` (${importable.toLocaleString()})` : ''}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportResult({ result, listLabel }: { result: ImportResultResponse; listLabel: string }) {
  const parts = [
    `${result.added.toLocaleString()} added`,
    `${result.updated.toLocaleString()} updated`,
    ...(result.mode === 'replace' ? [`${result.removed.toLocaleString()} removed`] : []),
    `${result.unchanged.toLocaleString()} already on the list`,
    `${result.skipped.toLocaleString()} skipped`,
  ];
  return (
    <div role="status" className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-900">
      <p className="font-medium">
        {result.mode === 'replace' ? `${listLabel} replaced` : `Imported into the ${listLabel}`}
      </p>
      <p className="mt-1">{parts.join(' · ')}</p>
      <p className="mt-1">
        {result.rescan_scheduled
          ? 'Open cases are being re-scanned once with the new list.'
          : 'Nothing changed, so no re-scan was needed.'}
      </p>
    </div>
  );
}

interface ImportPreviewProps {
  preview: ImportPreviewResponse;
  disabled: boolean;
  onMappingChange: (mapping: ImportColumnMapping) => void;
}

function ImportPreview({ preview, disabled, onMappingChange }: ImportPreviewProps) {
  const { mapping, columns, totals } = preview;
  const headerId = useId();
  const named = columns.filter((c) => c.header).map((c) => c.index);
  const included = mapping.category_columns.length ? mapping.category_columns : named;

  const columnSelect = (
    role: 'phrase_column' | 'category_column' | 'match_column',
    label: string,
    optional: boolean
  ) => (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <Select
        value={mapping[role] === null ? NONE : String(mapping[role])}
        onValueChange={(v) => onMappingChange(withColumn(mapping, role, v === NONE ? null : Number(v)))}
        disabled={disabled}
      >
        <SelectTrigger aria-label={label} className="w-[200px] bg-white">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {optional && <SelectItem value={NONE}>None</SelectItem>}
          {columns.map((c) => (
            <SelectItem key={c.index} value={String(c.index)}>
              {c.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="flex flex-col gap-4" data-testid="allow-list-import-preview">
      {/* Mapping */}
      <section className="rounded-lg border border-border p-4" aria-labelledby={`${headerId}-mapping`}>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h3 id={`${headerId}-mapping`} className="text-sm font-medium">
            Column mapping{preview.file_name ? ` · ${preview.file_name}` : ''}
          </h3>
          <span
            className="inline-flex items-center gap-1 text-xs text-muted-foreground"
            data-testid="mapping-source"
            data-source={preview.mapping_source}
          >
            {preview.mapping_source === 'llm' && <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />}
            {SOURCE_TEXT[preview.mapping_source]}
          </span>
        </div>

        <div className="mt-3 flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">Layout</span>
            <Select
              value={mapping.layout}
              onValueChange={(v) => onMappingChange(withLayout(mapping, v as ImportColumnMapping['layout']))}
              disabled={disabled}
            >
              <SelectTrigger aria-label="Layout" className="w-[230px] bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rows">One phrase per row</SelectItem>
                <SelectItem value="columns_per_category">One column per category</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {mapping.layout === 'rows' && (
            <>
              {columnSelect('phrase_column', 'Phrase column', false)}
              {columnSelect('category_column', 'Category column', true)}
              {columnSelect('match_column', 'Match column', true)}
              <label className="flex items-center gap-2 pb-2 text-sm">
                <Checkbox
                  checked={mapping.has_header}
                  onCheckedChange={(v) => onMappingChange({ ...mapping, has_header: v === true })}
                  disabled={disabled}
                />
                First row is a header
              </label>
            </>
          )}
        </div>

        {mapping.layout === 'columns_per_category' && (
          <fieldset className="mt-3" disabled={disabled}>
            <legend className="text-xs font-medium text-muted-foreground mb-1">Columns to import</legend>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {columns
                .filter((c) => c.header)
                .map((c) => {
                  const checked = included.includes(c.index);
                  return (
                    <label key={c.index} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={checked}
                        disabled={disabled || (checked && included.length === 1)}
                        onCheckedChange={(v) =>
                          onMappingChange(withCategoryColumn(mapping, named, c.index, v === true))
                        }
                      />
                      {c.header}
                    </label>
                  );
                })}
            </div>
          </fieldset>
        )}

        {preview.mapping_problems.length > 0 && (
          <div role="alert" className="mt-3 flex gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
            <ul>
              {preview.mapping_problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {preview.mapping_problems.length === 0 && (
        <>
          <p className="text-sm" data-testid="import-totals">
            <strong>{(totals.phrases ?? 0).toLocaleString()}</strong> {plural(totals.phrases, 'phrase')} ·{' '}
            <span className="text-green-800">{(totals.new ?? 0).toLocaleString()} new</span> ·{' '}
            {(totals.update ?? 0).toLocaleString()} to update · {(totals.unchanged ?? 0).toLocaleString()} already on
            the list · <span className="text-amber-800">{(totals.duplicate ?? 0).toLocaleString()} {plural(totals.duplicate, 'duplicate')}</span> ·{' '}
            <span className="text-orange-800">{(totals.conflict ?? 0).toLocaleString()} on the other list</span> ·{' '}
            <span className="text-red-700">{(totals.error ?? 0).toLocaleString()} {plural(totals.error, 'error')}</span>
          </p>

          {/* Categories with their match type */}
          <section aria-labelledby={`${headerId}-categories`}>
            <h3 id={`${headerId}-categories`} className="text-sm font-medium mb-2">
              Categories
            </h3>
            <div className="overflow-hidden rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-muted-foreground">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left font-medium">
                      Category
                    </th>
                    <th scope="col" className="px-3 py-2 text-left font-medium w-[150px]">
                      Match
                    </th>
                    <th scope="col" className="px-3 py-2 text-right font-medium">
                      Phrases
                    </th>
                    <th scope="col" className="px-3 py-2 text-right font-medium">
                      New
                    </th>
                    <th scope="col" className="px-3 py-2 text-right font-medium">
                      Skipped
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {preview.categories.map((c) => (
                    <tr key={c.category ?? NONE}>
                      <td className="px-3 py-2 break-words">
                        {c.category ?? <span className="text-muted-foreground">No category</span>}
                      </td>
                      <td className="px-3 py-1.5">
                        <Select
                          value={c.match}
                          onValueChange={(v) => onMappingChange(withCategoryMatch(mapping, c.category, v as ImportMatch))}
                          disabled={disabled}
                        >
                          <SelectTrigger size="sm" aria-label={`Match for ${c.category ?? 'phrases without a category'}`} className="w-[130px] bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="exact">Exact</SelectItem>
                            <SelectItem value="contains">Contains</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{c.total.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{c.new.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {(c.duplicate + c.conflict + c.error).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              A match column in the file wins over the category&apos;s match.
            </p>
          </section>

          {preview.issues.length > 0 && (
            <RowTable
              title={`Duplicates and problems (${(
                (totals.duplicate ?? 0) +
                (totals.conflict ?? 0) +
                (totals.error ?? 0)
              ).toLocaleString()})`}
              rows={preview.issues}
              truncated={preview.issues_truncated}
              testId="import-issues"
            />
          )}
          <RowTable
            title={`Phrases${preview.rows.length > ROWS_SHOWN || preview.rows_truncated ? ` (first ${ROWS_SHOWN})` : ''}`}
            rows={preview.rows.slice(0, ROWS_SHOWN)}
            truncated={false}
            testId="import-rows"
          />
        </>
      )}
    </div>
  );
}

function RowTable({
  title,
  rows,
  truncated,
  testId,
}: {
  title: string;
  rows: ImportPreviewRow[];
  truncated: boolean;
  testId: string;
}) {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} data-testid={testId}>
      <h3 id={headingId} className="text-sm font-medium mb-2">
        {title}
      </h3>
      <div className="max-h-64 overflow-y-auto rounded-lg border border-border">
        <table className="w-full table-fixed text-sm">
          <thead className="sticky top-0 bg-muted text-muted-foreground">
            <tr>
              <th scope="col" className="px-3 py-2 text-left font-medium w-16">
                Line
              </th>
              <th scope="col" className="px-3 py-2 text-left font-medium">
                Phrase
              </th>
              <th scope="col" className="px-3 py-2 text-left font-medium w-[22%]">
                Category
              </th>
              <th scope="col" className="px-3 py-2 text-left font-medium w-24">
                Match
              </th>
              <th scope="col" className="px-3 py-2 text-left font-medium w-[26%]">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r, i) => (
              <tr key={`${r.line}-${r.column ?? ''}-${i}`}>
                <td className="px-3 py-1.5 tabular-nums text-muted-foreground">{r.line}</td>
                <td className="px-3 py-1.5 font-mono break-words">
                  {r.phrase || <span className="text-muted-foreground">(empty)</span>}
                </td>
                <td className="px-3 py-1.5 break-words text-muted-foreground">{r.category ?? '—'}</td>
                <td className="px-3 py-1.5 text-muted-foreground">{r.match === 'exact' ? 'Exact' : 'Contains'}</td>
                <td className="px-3 py-1.5">
                  <span className={`inline-block rounded border px-1.5 py-0.5 text-xs ${STATUS_CLASS[r.status]}`}>
                    {STATUS_LABELS[r.status]}
                  </span>
                  {r.message && r.status !== 'unchanged' && (
                    <span className="block text-xs text-muted-foreground mt-0.5">{r.message}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {truncated && <p className="mt-1 text-xs text-muted-foreground">Only the first rows are shown.</p>}
    </section>
  );
}
